import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ZAxis,
  ReferenceLine,
  ReferenceArea,
} from "recharts";

const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const STOCK_MACRO_EXPOSURES_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_macro_exposures.json";

const DEFAULT_FILTERS = {
  minMarketCap: 5,
  maxMarketCap: "",
  minForwardPE: "",
  maxForwardPE: "",
  minPB: "",
  maxPB: "",
  minBeta: "",
  maxBeta: "",
};

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function normalizeTicker(s) {
  return String(s || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.-]/g, "");
}

function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,%]/g, "").replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function formatLargeMoneyNoDecimals(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000_000) return `$${Math.round(value / 1_000_000_000_000)}T`;
  if (abs >= 1_000_000_000) return `$${Math.round(value / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `$${Math.round(value / 1_000_000)}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000)}K`;

  return `$${Math.round(value)}`;
}

function formatNoDecimals(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return String(Math.round(n));
}

function formatPercentNoDecimals(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

function normalizeScreenerPayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function normalizeMacroPayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

function getStockName(row) {
  return String(row?.name ?? row?.stock ?? row?.company ?? row?.companyName ?? "").trim();
}

function getTicker(row) {
  return normalizeTicker(row?.ticker ?? row?.Ticker ?? row?.symbol ?? row?.Symbol);
}

function getFirstNumeric(row, keys) {
  for (const key of keys) {
    const v = parseNumber(row?.[key]);
    if (Number.isFinite(v)) return v;
  }

  const normalizedLookup = new Map();
  for (const realKey of Object.keys(row || {})) {
    normalizedLookup.set(
      String(realKey).toLowerCase().replace(/[^a-z0-9]/g, ""),
      realKey
    );
  }

  for (const key of keys) {
    const normalized = String(key).toLowerCase().replace(/[^a-z0-9]/g, "");
    const realKey = normalizedLookup.get(normalized);
    if (realKey) {
      const v = parseNumber(row?.[realKey]);
      if (Number.isFinite(v)) return v;
    }
  }

  return null;
}

function getMacroExposure(row, dimension) {
  const dim = String(dimension).toLowerCase();

  return getFirstNumeric(row, [
    `${dim}_score`,
    `${dim}_exposure`,
    `${dim}_exposure_score`,
    `${dim}_beta`,
    `${dim}_corr`,
    `${dim}_correlation`,
    `${dim}Score`,
    `${dim}Exposure`,
    `${dim}Beta`,
    `${dim}Corr`,
    `${dim}Correlation`,
  ]);
}

function normalizeExposureForChart(value, allValues) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;

  const clean = allValues.filter(Number.isFinite);
  if (!clean.length) return v;

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const maxAbs = Math.max(...clean.map((x) => Math.abs(x))) || 1;

  // If the data is percentile-like, center it so:
  // 0% = -1, 50% = 0, 100% = +1.
  // This creates a true four-quadrant chart instead of pushing everything top-right.
  if (min >= 0 && max <= 1) {
    return v * 2 - 1;
  }

  // Same idea if the data is stored as 0-100 instead of 0-1.
  if (min >= 0 && max <= 100) {
    return v / 50 - 1;
  }

  // If data is signed but larger than -1 to +1, normalize by max absolute value.
  if (maxAbs > 2) {
    return v / maxAbs;
  }

  // Already signed around zero.
  return v;
}

function normalizePctAboveLowValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;

  // Handles both decimal format and whole-percent format:
  // 0.08 = 8%, 1.05 = 105%, 105 = 105%.
  if (Math.abs(n) > 2) return n / 100;

  return n;
}

function getPctAbove52WeekLow(row) {
  const price = getFirstNumeric(row, ["price", "currentPrice", "current_price"]);
  const low = getFirstNumeric(row, ["fiftyTwoWeekLow", "52WeekLow", "52_week_low", "yearLow"]);

  // Prefer the raw price/low calculation because precomputed % fields can be inconsistently scaled.
  if (Number.isFinite(price) && Number.isFinite(low) && low > 0) {
    return price / low - 1;
  }

  const direct = getFirstNumeric(row, [
    "pctAbove52WeekLow",
    "percentAbove52WeekLow",
    "pct_above_52_week_low",
    "percentageRelativeTo52WeekLow",
    "percentage_relative_to_52_week_low",
    "relativeTo52WeekLow",
  ]);

  return normalizePctAboveLowValue(direct);
}

function colorByPctAboveLow(pctAboveLow) {
  const p = Number(pctAboveLow);
  if (!Number.isFinite(p)) return "#64748b";
  if (p <= 0.1) return "#16a34a";
  if (p <= 0.3) return "#ca8a04";
  if (p > 0.5) return "#dc2626";
  return "#f97316";
}

function getLowProximityLabel(pctAboveLow) {
  const p = Number(pctAboveLow);
  if (!Number.isFinite(p)) return "Unknown";
  if (p <= 0.1) return "Within 10% of 52W low";
  if (p <= 0.3) return "Within 30% of 52W low";
  if (p > 0.5) return "Over 50% above 52W low";
  return "30–50% above 52W low";
}

function passesRange(value, min, max, scale = 1) {
  const v = Number(value);
  if (!Number.isFinite(v)) return false;

  const hasMin = min !== "" && min !== null && min !== undefined;
  const hasMax = max !== "" && max !== null && max !== undefined;

  const minVal = hasMin ? Number(min) * scale : null;
  const maxVal = hasMax ? Number(max) * scale : null;

  if (hasMin && Number.isFinite(minVal) && v < minVal) return false;
  if (hasMax && Number.isFinite(maxVal) && v > maxVal) return false;

  return true;
}

// function getDomain(values) {
//   const clean = values.filter(Number.isFinite);
//   if (!clean.length) return [-1, 1];

//   const min = Math.min(...clean);
//   const max = Math.max(...clean);
//   const span = max - min || 1;
//   const pad = Math.max(span * 0.16, 0.08);

//   return [min - pad, max + pad];
// }

function MacroTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const p = payload[0]?.payload;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 13,
        boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
        fontSize: 12,
        color: "#0f172a",
        maxWidth: 320,
      }}
    >
      <div style={{ fontWeight: 950, fontSize: 16, marginBottom: 4 }}>{p?.ticker}</div>
      {p?.name ? <div style={{ color: "#64748b", marginBottom: 9 }}>{p.name}</div> : null}
      <div>Market cap: {formatLargeMoneyNoDecimals(p?.marketCap)}</div>
      <div>Forward P/E: {formatNoDecimals(p?.forwardPE)}</div>
      <div>P/B: {formatNoDecimals(p?.pb)}</div>
      <div>Beta: {formatNoDecimals(p?.beta)}</div>
      <div>Inflation exposure: {formatNoDecimals(p?.inflation * 100)}</div>
      <div>Growth exposure: {formatNoDecimals(p?.growth * 100)}</div>
      <div style={{ marginTop: 9, fontWeight: 950, color: p?.color }}>
        {getLowProximityLabel(p?.pctAbove52WeekLow)}
      </div>
      <div>
        Above 52W low:{" "}
        {Number.isFinite(p?.pctAbove52WeekLow)
          ? formatPercentNoDecimals(p.pctAbove52WeekLow)
          : "—"}
      </div>
    </div>
  );
}

function MacroDot({ cx, cy, payload, isMobile }) {
  if (cx == null || cy == null) return null;

  const isGreen = Number(payload?.pctAbove52WeekLow) <= 0.1;
  const r = isMobile ? 4 : Math.max(4, Math.min(13, payload.size || 5));

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={payload.color}
        stroke={isGreen ? "#064e3b" : "#ffffff"}
        strokeWidth={isGreen ? 2 : 1.4}
        opacity={0.92}
      />
      {isGreen ? (
        <text
          x={cx + r + 5}
          y={cy + 3}
          fontSize={isMobile ? 8 : 10}
          fill="#14532d"
          fontWeight={950}
          pointerEvents="none"
        >
          {payload.ticker}
        </text>
      ) : null}
    </g>
  );
}

function FilterInput({ label, value, onChange }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label}
      type="number"
      style={filterInput}
    />
  );
}

export default function Clusters() {
  const isMobile = useIsMobile(900);

  const [screenerRows, setScreenerRows] = useState([]);
  const [macroRows, setMacroRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setErr("");

        const [screenerRes, macroRes] = await Promise.all([
          fetch(STOCK_SCREENER_URL, { cache: "no-store" }),
          fetch(STOCK_MACRO_EXPOSURES_URL, { cache: "no-store" }),
        ]);

        const [screenerText, macroText] = await Promise.all([
          screenerRes.text(),
          macroRes.text(),
        ]);

        if (!screenerRes.ok || screenerText.trim().startsWith("<")) {
          throw new Error(
            `Could not load stock_screener.json. First 120 chars: ${screenerText.slice(
              0,
              120
            )}`
          );
        }

        if (!macroRes.ok || macroText.trim().startsWith("<")) {
          throw new Error(
            `Could not load stock_macro_exposures.json. First 120 chars: ${macroText.slice(
              0,
              120
            )}`
          );
        }

        if (alive) {
          setScreenerRows(normalizeScreenerPayload(JSON.parse(screenerText)));
          setMacroRows(normalizeMacroPayload(JSON.parse(macroText)));
        }
      } catch (e) {
        if (alive) setErr(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const screenerByTicker = useMemo(() => {
    const map = new Map();

    for (const row of screenerRows) {
      const ticker = getTicker(row);
      if (!ticker) continue;

      map.set(ticker, {
        raw: row,
        ticker,
        name: getStockName(row),
        marketCap: parseNumber(row?.marketCap ?? row?.market_cap ?? row?.MarketCap),
        forwardPE: getFirstNumeric(row, ["forwardPE", "forward_pe", "ForwardPE"]),
        pb: getFirstNumeric(row, ["pb", "priceToBook", "price_to_book", "P/B"]),
        beta: getFirstNumeric(row, ["beta", "Beta"]),
        pctAbove52WeekLow: getPctAbove52WeekLow(row),
      });
    }

    return map;
  }, [screenerRows]);

  const macroByTickerRaw = useMemo(() => {
    const map = new Map();

    for (const row of macroRows) {
      const ticker = getTicker(row);
      if (!ticker) continue;

      map.set(ticker, {
        raw: row,
        ticker,
        inflationRaw: getMacroExposure(row, "inflation"),
        growthRaw: getMacroExposure(row, "growth"),
        liquidityRaw: getMacroExposure(row, "liquidity"),
      });
    }

    return map;
  }, [macroRows]);

  const macroScaleValues = useMemo(() => {
    const vals = [];

    for (const row of macroByTickerRaw.values()) {
      if (Number.isFinite(row.inflationRaw)) vals.push(row.inflationRaw);
      if (Number.isFinite(row.growthRaw)) vals.push(row.growthRaw);
      if (Number.isFinite(row.liquidityRaw)) vals.push(row.liquidityRaw);
    }

    return vals;
  }, [macroByTickerRaw]);

  const macroScatterData = useMemo(() => {
    const rows = [];

    for (const [ticker, stock] of screenerByTicker.entries()) {
      if (
        !passesRange(stock.marketCap, filters.minMarketCap, filters.maxMarketCap, 1_000_000_000) ||
        !passesRange(stock.forwardPE, filters.minForwardPE, filters.maxForwardPE) ||
        !passesRange(stock.pb, filters.minPB, filters.maxPB) ||
        !passesRange(stock.beta, filters.minBeta, filters.maxBeta)
      ) {
        continue;
      }

      const macro = macroByTickerRaw.get(ticker);
      if (!macro) continue;

      const inflation = normalizeExposureForChart(macro.inflationRaw, macroScaleValues);
      const growth = normalizeExposureForChart(macro.growthRaw, macroScaleValues);
      const liquidity = normalizeExposureForChart(macro.liquidityRaw, macroScaleValues);

      if (!Number.isFinite(inflation) || !Number.isFinite(growth)) continue;

      const marketCap = stock.marketCap;
      const size =
        Number.isFinite(marketCap)
          ? Math.max(4, Math.min(17, Math.log10(Math.max(10, marketCap)) - 5))
          : 6;

      rows.push({
        ticker,
        name: stock.name,
        marketCap,
        forwardPE: stock.forwardPE,
        pb: stock.pb,
        beta: stock.beta,
        inflation,
        growth,
        liquidity,
        pctAbove52WeekLow: stock.pctAbove52WeekLow,
        color: colorByPctAboveLow(stock.pctAbove52WeekLow),
        size,
        z: 1,
      });
    }

    return rows.sort((a, b) => b.marketCap - a.marketCap);
  }, [screenerByTicker, macroByTickerRaw, macroScaleValues, filters]);

  // const greenCount = useMemo(
  //   () => macroScatterData.filter((x) => Number(x.pctAbove52WeekLow) <= 0.1).length,
  //   [macroScatterData]
  // );

  const macroHeight = isMobile ? 650 : 790;

  if (loading) {
    return (
      <div style={pageShell}>
        <div style={messageCard}>Loading macro map…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div style={pageShell}>
        <div style={errorCard}>Error: {err}</div>
      </div>
    );
  }

  return (
    <div style={pageShell}>
      <div style={{ maxWidth: 1500, margin: "0 auto", width: "100%" }}>
        <section style={heroCard}>
          <div style={eyebrow}>Majorah Macro Factor Map</div>
          <h1 style={title}>Inflation × Growth Exposure Map</h1>
          <p style={subtitle}>
            Filter by valuation and size. X-axis is inflation exposure, Y-axis is growth exposure,
            and colors show distance from each stock’s 52-week low. Green tickers are labeled.
          </p>

          <div style={statGrid}>
{/*            <HeroStat label="Filtered Stocks" value={macroScatterData.length} />
            <HeroStat label="Near 52W Low" value={greenCount} />
            <HeroStat label="Min Market Cap" value={`${filters.minMarketCap || 0}B`} />
            <HeroStat label="Max Fwd P/E" value={filters.maxForwardPE || "Any"} />
            <HeroStat label="Max P/B" value={filters.maxPB || "Any"} />
*/}          </div>
        </section>

        <section style={filterCard}>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, fontWeight: 950, color: "#0f172a" }}>
              Filters
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              Market cap is entered in billions. All chart values display without decimals.
            </div>
          </div>

          <div style={filterGrid}>
            <FilterInput
              label="Min Mkt Cap $B"
              value={filters.minMarketCap}
              onChange={(v) => updateFilter("minMarketCap", v)}
            />
            <FilterInput
              label="Max Mkt Cap $B"
              value={filters.maxMarketCap}
              onChange={(v) => updateFilter("maxMarketCap", v)}
            />
            <FilterInput
              label="Min Fwd P/E"
              value={filters.minForwardPE}
              onChange={(v) => updateFilter("minForwardPE", v)}
            />
            <FilterInput
              label="Max Fwd P/E"
              value={filters.maxForwardPE}
              onChange={(v) => updateFilter("maxForwardPE", v)}
            />
            <FilterInput
              label="Min P/B"
              value={filters.minPB}
              onChange={(v) => updateFilter("minPB", v)}
            />
            <FilterInput
              label="Max P/B"
              value={filters.maxPB}
              onChange={(v) => updateFilter("maxPB", v)}
            />
            <FilterInput
              label="Min Beta"
              value={filters.minBeta}
              onChange={(v) => updateFilter("minBeta", v)}
            />
            <FilterInput
              label="Max Beta"
              value={filters.maxBeta}
              onChange={(v) => updateFilter("maxBeta", v)}
            />
            <button
              style={clearButton}
              onClick={() => setFilters(DEFAULT_FILTERS)}
            >
              Reset Filters
            </button>
          </div>
        </section>

        <section style={legendCard}>
          <div style={legendItem}>
            <span style={{ ...legendDot, background: "#16a34a" }} />
            <span>Within 10% of 52W low</span>
          </div>
          <div style={legendItem}>
            <span style={{ ...legendDot, background: "#ca8a04" }} />
            <span>Within 30% of 52W low</span>
          </div>
          <div style={legendItem}>
            <span style={{ ...legendDot, background: "#f97316" }} />
            <span>30–50% above 52W low</span>
          </div>
          <div style={legendItem}>
            <span style={{ ...legendDot, background: "#dc2626" }} />
            <span>Over 50% above 52W low</span>
          </div>
        </section>

        <section style={chartCard}>
          <div style={chartToolbar}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 950, color: "#0f172a" }}>
                Macro Exposure Map
              </div>
              <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
                Top-right = inflationary growth. Top-left = stagflation-type. Bottom-right = deflationary growth.
              </div>
            </div>
          </div>

          {macroScatterData.length === 0 ? (
            <div
              style={{
                padding: 18,
                borderRadius: 14,
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              No stocks match these filters.
            </div>
          ) : (
            <div style={{ width: "100%", height: macroHeight, minHeight: macroHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{
                    top: 28,
                    right: isMobile ? 8 : 32,
                    bottom: 38,
                    left: isMobile ? 8 : 34,
                  }}
                >
                  <defs>
                    <linearGradient id="growthBg" x1="0" y1="1" x2="1" y2="0">
                      <stop offset="0%" stopColor="#eff6ff" stopOpacity={0.9} />
                      <stop offset="50%" stopColor="#ffffff" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#fef3c7" stopOpacity={0.85} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <ReferenceArea
                    x1={-1}
                    x2={1}
                    y1={-1}
                    y2={1}
                    fill="url(#growthBg)"
                    fillOpacity={0.45}
                  />
                  <ReferenceLine
                    x={0}
                    stroke="#0f172a"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <ReferenceLine
                    y={0}
                    stroke="#0f172a"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <XAxis
                    type="number"
                    dataKey="inflation"
                    domain={[-1, 1]}
                    name="Inflation"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickFormatter={(v) => formatNoDecimals(Number(v) * 100)}
                    label={{
                      value: "Inflation Exposure",
                      position: "insideBottom",
                      offset: -20,
                      fill: "#334155",
                      fontSize: 12,
                      fontWeight: 900,
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="growth"
                    domain={[-1, 1]}
                    name="Growth"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickFormatter={(v) => formatNoDecimals(Number(v) * 100)}
                    label={{
                      value: "Growth Exposure",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#334155",
                      fontSize: 12,
                      fontWeight: 900,
                    }}
                  />
                  <ZAxis type="number" dataKey="z" range={[50, 90]} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<MacroTooltip />} />
                  <Scatter
                    name="Stocks"
                    data={macroScatterData}
                    shape={(props) => (
                      <MacroDot {...props} isMobile={isMobile} />
                    )}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// function HeroStat({ label, value }) {
//   return (
//     <div
//       style={{
//         padding: 12,
//         borderRadius: 16,
//         background: "rgba(255,255,255,0.1)",
//         border: "1px solid rgba(255,255,255,0.16)",
//         boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
//       }}
//     >
//       <div
//         style={{
//           fontSize: 10,
//           letterSpacing: "0.08em",
//           textTransform: "uppercase",
//           color: "#cbd5e1",
//           fontWeight: 800,
//           marginBottom: 6,
//         }}
//       >
//         {label}
//       </div>
//       <div style={{ fontSize: 20, fontWeight: 950, letterSpacing: "-0.04em" }}>
//         {value}
//       </div>
//     </div>
//   );
// }

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: 16,
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  borderRadius: 28,
  padding: 24,
  background:
    "radial-gradient(circle at 15% 10%, rgba(59,130,246,0.26), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
  marginBottom: 18,
  overflow: "hidden",
};

const eyebrow = {
  fontSize: 10,
  fontWeight: 900,
  color: "#93c5fd",
  letterSpacing: "0.13em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const title = {
  margin: 0,
  fontSize: 24,
  lineHeight: 1,
  letterSpacing: "-0.06em",
};

const subtitle = {
  margin: "10px 0 0",
  maxWidth: 940,
  color: "#cbd5e1",
  fontSize: 12,
  lineHeight: 1.25,
};

const statGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 10,
  marginTop: 20,
};

const filterCard = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  background: "rgba(255,255,255,0.92)",
  padding: 8,
  marginBottom: 6,
  boxShadow: "0 14px 32px rgba(15,23,42,0.08)",
};

const filterGrid = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 9,
  flexWrap: "wrap",
};

const filterInput = {
  width: 128,
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  padding: "10px 11px",
  background: "white",
  color: "#0f172a",
  fontSize: 12,
  fontWeight: 800,
  outline: "none",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const clearButton = {
  border: "1px solid #0f172a",
  borderRadius: 13,
  background: "#0f172a",
  color: "white",
  padding: "10px 12px",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(15,23,42,0.16)",
};

const legendCard = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  background: "rgba(255,255,255,0.9)",
  padding: 12,
  marginBottom: 18,
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const legendItem = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontSize: 12,
  color: "#334155",
  fontWeight: 800,
};

const legendDot = {
  width: 11,
  height: 11,
  borderRadius: 999,
  display: "inline-block",
};

const chartCard = {
  border: "1px solid #e2e8f0",
  borderRadius: 26,
  background: "white",
  padding: 18,
  boxShadow: "0 18px 44px rgba(15,23,42,0.11)",
  overflow: "hidden",
  marginBottom: 18,
};

const chartToolbar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 12,
};

const messageCard = {
  maxWidth: 900,
  margin: "0 auto",
  padding: 18,
  borderRadius: 16,
  background: "white",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

const errorCard = {
  maxWidth: 900,
  margin: "0 auto",
  padding: 18,
  borderRadius: 16,
  background: "#fff1f2",
  border: "1px solid #fecdd3",
  color: "#9f1239",
};
